import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Table,
  Form,
  Input,
  Modal,
  Select,
  message,
  Upload,
  Tag,
  Tooltip,
  Image,
  Space,
} from "antd";
import { fetchContainers } from "../containers/FetchContainers";
import { fetchTAT } from "../tat/FetchTAT";
import { fetchTestRequirement } from "../testRequirements/FetchTestRequirement";
import { PlusCircleOutlined, UploadOutlined } from "@ant-design/icons";
import { fetchTestTag } from "../testTags/FetchTestTags";
import { fetchTestCategory } from "../testCategory/FetchTestCategory";
import { useSelector } from "react-redux";
import { fetchTest } from "./FetchTest";
import { fetchLab } from "../labPartner/FetchLab";
import { EditOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { selectBaseUrl, selectProdTestUrl } from "../../redux/slices/envSlice";
import BulkUploadTest from "./BulkUploadTest";
import Highlighter from "react-highlight-words";
import { token } from "../../constants/Constants";
import SecureImage from "../wellnessExecutive/SecureImage";
import {
  create_lab_test,
  fetch_test_departments,
  get_paginated_tests,
  update_lab_test,
} from "../../endpoints/API";

const { Option } = Select;

const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const Test = () => {
  const baseUrl = useSelector(selectBaseUrl);
  const prodTestUrl = useSelector(selectProdTestUrl);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [form] = Form.useForm();
  const [containers, setContainers] = useState([]);
  const [TAT, setTAT] = useState([]);
  const [TestRequirements, setTestRequirements] = useState([]);
  const [Lab, setLab] = useState([]);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [categoryForm] = Form.useForm();
  const [testTags, setTestTags] = useState([]);
  const [testCategory, setTestCategory] = useState([]);
  const isUser = useSelector((state) => state.user.user);
  const [test, setTest] = useState([]);
  const [editTest, setEditTest] = useState(null);
  const [bulkUploadForm] = Form.useForm();
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const [currentSearch, setCurrentSearch] = useState("");
  const [count, setCount] = useState(0);
  const [removeIcon, setRemoveIcon] = useState(false);
  const [testDepartments, setTestDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("You can only upload image files.");
    }
    return isImage;
  };

  const handleChange = ({ fileList: newFileList }) => setFileList(newFileList);

  const handleCustomRequest = async ({ file, onSuccess, onError }) => {
    try {
      const base64 = await getBase64(file);
      onSuccess("ok");
    } catch (error) {
      onError(error);
    }
  };

  const performSearch = (value) => {
    const trimmed = (value || "").trim();
    setCurrentSearch(trimmed);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (pag) => {
    setPagination((prev) => ({
      ...prev,
      current: pag.current,
      pageSize: pag.pageSize,
    }));
  };

  const getColumnSearchProps = (dataIndex, title) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          placeholder={`Search ${title}`}
          value={selectedKeys[0] || ""}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => {
            confirm();
            setCurrentSearch(selectedKeys[0] || "");
          }}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            onClick={() => {
              confirm();
              setCurrentSearch(selectedKeys[0] || "");
            }}
          >
            Search
          </Button>
          <Button
            size="small"
            onClick={() => {
              clearFilters?.();
              setSelectedKeys([]);
              setCurrentSearch("");
              confirm({ closeDropdown: false });
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: () => (
      <SearchOutlined style={{ color: currentSearch ? "#1677ff" : undefined }} />
    ),
  });

  useEffect(() => {
    const loadInitialData = async () => {
      await fetchTAT(setTAT, baseUrl, prodTestUrl);
      await fetchTestRequirement(setTestRequirements, baseUrl, prodTestUrl);
      await fetchContainers(setContainers, baseUrl, prodTestUrl);
      await fetchLab(setLab, baseUrl, prodTestUrl);
      await fetchTestTag(setTestTags, baseUrl, prodTestUrl);
      await fetchTestCategory(setTestCategory, baseUrl, prodTestUrl);

      const deptRes = await fetch_test_departments();
      setTestDepartments(deptRes.data);

      const pathologyDept = deptRes.data.find((d) => d.name === "pathology");
      if (pathologyDept) {
        setSelectedDepartment(pathologyDept.id);
        form.setFieldsValue({ test_department: pathologyDept.id });
      }
    };
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedDepartment !== null) {
      fetchPaginationTest(currentSearch, pagination.current, pagination.pageSize);
    }
  }, [currentSearch, pagination.current, pagination.pageSize, selectedDepartment]);

  const fetchPaginationTest = async (search = "", page = 1, pageSize = 10) => {
    try {
      setLoading(true);
      const response = await get_paginated_tests(
        search,
        page,
        pageSize,
        selectedDepartment
      );
      const results = Array.isArray(response.data.results)
        ? response.data.results
        : [];
      setTest(results);
      setCount(response?.data?.count);
      setPagination({ current: page, pageSize, total: response.data.count || 0 });
    } catch (error) {
      console.error("Failed to fetch tests:", error);
      message.error("Failed to load tests");
      setTest([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpload = (values) => {
    setIsBulkUploadModalOpen(false);
  };

  const handleBulkUploadCancel = () => {
    setIsBulkUploadModalOpen(false);
  };

  const handleEdit = (record) => {
    setEditTest(record);
    setIsFormOpen(true);
    form.setFieldsValue({
      name: record.name,
      tat: record.tat?.id,
      testRequirements: record.test_requirements?.map((req) => req.id),
      containers: record.containers?.map((container) => container.id),
      lab: record.lab?.id,
      b2cPrice: record.b2c_price,
      mrp: record.mrp,
      b2bPrice: record.b2b_price,
      tags: record.tags?.map((tag) => tag.id),
      priority: record.priority,
      testCategory: record.test_category?.map((category) => category.id),
      description: record.description,
      testId: record.testId,
      also_known_as: record.also_known_as,
      test_department: record.test_department?.id,
    });
    if (record.icon) {
      setFileList([{ url: record.icon }]);
    } else {
      setFileList([]);
    }
  };

  const columns = [
    {
      title: "Test Image",
      dataIndex: "icon",
      key: "icon",
      render: (icon, record) => (
        <SecureImage key={`cert-${record.id}`} path={icon} width={100} />
      ),
    },
    {
      title: "Test ID",
      dataIndex: "testId",
      key: "testId",
      ...getColumnSearchProps("testId", "Test ID"),
    },
    {
      title: "Test Name",
      dataIndex: "name",
      key: "name",
      ...getColumnSearchProps("name", "Test Name"),
    },
    {
      title: "Lab Name",
      dataIndex: ["lab", "name"],
      key: "lab_name",
      ...getColumnSearchProps("lab_name", "Lab Name"),
      render: (labName) => labName || "N/A",
    },
    {
      title: "TAT",
      dataIndex: "tat",
      key: "tat",
      render: (tat) =>
        tat ? (
          <Tag>
            {tat.tat_pickup_time} {tat.duration_unit}`
          </Tag>
        ) : (
          <Tag>N/A</Tag>
        ),
    },
    { title: "Priority", dataIndex: "priority", key: "priority" },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (description) => (
        <Tooltip title={description || "N/A"}>
          <div
            style={{
              width: "5rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {description || "N/A"}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Test Tags",
      dataIndex: "tags",
      key: "tags",
      render: (tags) =>
        tags && tags.length > 0
          ? tags.map((tag, index) => (
              <Tag color="blue" key={index} style={{ marginBottom: "4px" }}>
                {tag.name}
              </Tag>
            ))
          : "N/A",
    },
    {
      title: "Containers",
      dataIndex: "containers",
      key: "containers",
      render: (containers) =>
        containers && containers.length > 0
          ? containers.map((container, index) => (
              <Tag color="green" key={index} style={{ marginBottom: "4px" }}>
                {container.name}
              </Tag>
            ))
          : "N/A",
    },
    {
      title: "B2C Price",
      dataIndex: "b2c_price",
      key: "b2c_price",
      render: (b2c_price) => (b2c_price ? `₹${b2c_price}` : "N/A"),
    },
    {
      title: "MRP",
      dataIndex: "mrp",
      key: "mrp",
      render: (mrp) => (mrp ? `₹${mrp}` : "N/A"),
    },
    {
      title: "B2B Price",
      dataIndex: "b2b_price",
      key: "b2b_price",
      render: (b2b_price) => (b2b_price ? `₹${b2b_price}` : "N/A"),
    },
    {
      title: "Test Category",
      dataIndex: "test_category",
      key: "test_category",
      render: (test_category) =>
        test_category && test_category.length > 0 ? (
          test_category.map((category, index) => (
            <Tag color="gray" key={index} style={{ marginBottom: "4px" }}>
              {category.name}
            </Tag>
          ))
        ) : (
          <Tag color="gray">N/A</Tag>
        ),
    },
    {
      title: "Test Requirements",
      dataIndex: "test_requirements",
      key: "test_requirements",
      render: (test_requirements) => {
        const requirementsList =
          test_requirements && test_requirements.length > 0
            ? test_requirements.map((requirement, index) => (
                <div className=" border-b shadow-md mt-[.5rem]" key={index}>
                  {index + 1}. {requirement.name}
                </div>
              ))
            : "No requirements available";
        return (
          <Tooltip title={requirementsList}>
            <Button type="default" size="small">
              View Requirements
            </Button>
          </Tooltip>
        );
      },
    },
    {
      title: "Also Known As",
      dataIndex: "also_known_as",
      key: "also_known_as",
      render: (also_known_as) => (
        <Tooltip title={also_known_as ? also_known_as : "N/A"}>
          <Button type="default" size="small">
            View
          </Button>
        </Tooltip>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button type="link" onClick={() => handleEdit(record)}>
          <EditOutlined />
        </Button>
      ),
    },
  ];

  const handleFormSubmit = async (values) => {
    try {
      const requestData = {
        ...values,
        remove_icon: removeIcon,
        updated_by: isUser.user_id,
      };

      if (fileList.length > 0 && fileList[0].originFileObj) {
        requestData.icon = await getBase64(fileList[0].originFileObj);
        requestData.remove_icon = false;
      }

      let response;
      if (editTest) {
        console.log(requestData);
        response = await update_lab_test(editTest.id, requestData);
      } else {
        requestData.created_by = isUser.user_id;
        response = await create_lab_test(requestData);
      }

      if (response.statusText === "OK") {
        message.success(
          editTest ? "Test updated successfully!" : "Test created successfully!"
        );
        setIsFormOpen(false);
        form.resetFields();
        setEditTest(null);
        setFileList([]);
        setRemoveIcon(false);
        await fetchPaginationTest(
          currentSearch,
          pagination.current,
          pagination.pageSize
        );
      } else {
        message.error("Error submitting form: " + response.statusText);
      }
    } catch (error) {
      message.error(error?.response?.data?.error || "Network error.");
    }
  };

  const handleCancel = () => {
    setIsFormOpen(false);
    form.resetFields();
    setEditTest(null);
  };

  const handleCategorySubmit = (values) => {
    message.success("Category created successfully!");
    setIsCategoryModalOpen(false);
    categoryForm.resetFields();
  };

  const handleBulkUploadSuccess = () => {
    fetchPaginationTest(currentSearch, 1, pagination.pageSize);
  };

  const handleCategoryCancel = () => {
    setIsCategoryModalOpen(false);
    categoryForm.resetFields();
  };

  return (
    <div>
      {/* Header row — wraps on mobile */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          alignItems: "center",
          marginBottom: "1rem",
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 8,
            flex: 1,
            minWidth: 0,
          }}
        >
          <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
            Total Test:{" "}
            <Tag style={{ marginLeft: 4 }} color="green">
              {count}
            </Tag>
          </span>
          <Select
            style={{ width: 200, minWidth: 140 }}
            placeholder="Filter by Department"
            value={selectedDepartment}
            onChange={(value) => {
              setSelectedDepartment(value);
              setPagination((prev) => ({ ...prev, current: 1 }));
            }}
          >
            {testDepartments.map((dept) => (
              <Select.Option key={dept.id} value={dept.id}>
                {dept.name.toUpperCase()}
              </Select.Option>
            ))}
          </Select>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button type="primary" onClick={() => setIsBulkUploadModalOpen(true)}>
            Bulk Upload
          </Button>
          <Button type="primary" onClick={() => setIsFormOpen(true)}>
            Create Test
          </Button>
        </div>
      </div>

      {/* Table or Form */}
      {!isFormOpen ? (
        <div style={{ overflowX: "auto", width: "100%" }}>
          <Table
            dataSource={Array.isArray(test) ? test : []}
            columns={columns}
            rowKey="id"
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
            }}
            onChange={handleTableChange}
            loading={loading}
            scroll={{ x: "max-content" }}
          />
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormSubmit}
          style={{ maxWidth: "600px", width: "100%", margin: "0 auto" }}
        >
          <Form.Item label="Image" name="image">
            <Upload
              customRequest={handleCustomRequest}
              listType="picture-circle"
              fileList={fileList}
              onChange={handleChange}
              beforeUpload={beforeUpload}
              onPreview={handlePreview}
              maxCount={1}
            >
              {fileList.length >= 1 ? null : (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              )}
            </Upload>
            {editTest && fileList.length > 0 && (
              <Button
                danger
                size="small"
                onClick={() => {
                  setFileList([]);
                  setRemoveIcon(true);
                }}
                style={{ marginTop: "8px" }}
              >
                Remove Current Image
              </Button>
            )}
            {previewImage && (
              <Image
                wrapperStyle={{ display: "none" }}
                preview={{
                  visible: previewOpen,
                  onVisibleChange: (visible) => setPreviewOpen(visible),
                  afterOpenChange: (visible) => !visible && setPreviewImage(""),
                }}
                src={previewImage}
              />
            )}
          </Form.Item>

          <Form.Item
            label="Test ID"
            name="testId"
            rules={[{ required: true, message: "Please enter Test ID!" }]}
          >
            <Input placeholder="Enter Test ID" />
          </Form.Item>

          <Form.Item
            label="Test Name"
            name="name"
            rules={[{ required: true, message: "Please enter Test Name!" }]}
          >
            <Input placeholder="Enter Test Name" />
          </Form.Item>

          <Form.Item
            label="TAT"
            name="tat"
            rules={[{ required: true, message: "Please select TAT!" }]}
          >
            <Select placeholder="Select TAT">
              {TAT?.map((tat) => (
                <Select.Option key={tat.id} value={tat.id}>
                  {tat.tat_pickup_time + " " + tat.duration_unit}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Test Department"
            name="test_department"
            rules={[{ required: true, message: "Please select Test Department!" }]}
          >
            <Select placeholder="Select Department">
              {testDepartments.map((dept) => (
                <Select.Option key={dept.id} value={dept.id}>
                  {dept.name.toUpperCase()}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Test Requirements"
            name="testRequirements"
            rules={[{ required: true, message: "Please select Test Requirements!" }]}
          >
            <Select mode="multiple" placeholder="Select Test Requirements">
              {TestRequirements?.map((req) => (
                <Select.Option key={req.id} value={req.id}>
                  {req.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Containers"
            name="containers"
            rules={[{ required: true, message: "Please select Containers!" }]}
          >
            <Select mode="multiple" placeholder="Select Containers">
              {containers?.containers?.map((container) => (
                <Select.Option key={container.id} value={container.id}>
                  {container.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Lab"
            name="lab"
            rules={[{ required: true, message: "Please select a Lab!" }]}
          >
            <Select placeholder="Select Lab">
              {Lab?.map((labItem) => (
                <Select.Option key={labItem.id} value={labItem.id}>
                  {labItem.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="B2C Price"
            name="b2cPrice"
            rules={[{ required: true, message: "Please enter B2C Price!" }]}
          >
            <Input placeholder="Enter B2C Price" />
          </Form.Item>

          <Form.Item
            label="MRP"
            name="mrp"
            rules={[{ required: true, message: "Please enter MRP!" }]}
          >
            <Input placeholder="Enter MRP" />
          </Form.Item>

          <Form.Item
            label="B2B Price"
            name="b2bPrice"
            rules={[{ required: true, message: "Please enter B2B Price!" }]}
          >
            <Input placeholder="Enter B2B Price" />
          </Form.Item>

          <Form.Item
            label="Test Tags"
            name="tags"
            rules={[{ required: true, message: "Please enter Tags!" }]}
          >
            <Select mode="tags" placeholder="Enter Tags">
              {testTags?.map((tag) => (
                <Select.Option key={tag.id} value={tag.id}>
                  {tag.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Priority"
            name="priority"
            rules={[{ required: true, message: "Please enter Priority!" }]}
          >
            <Input placeholder="Enter Priority" />
          </Form.Item>

          <Form.Item
            label="Test Category"
            name="testCategory"
            rules={[{ required: true, message: "Please select a Category!" }]}
          >
            <Select placeholder="Select Category">
              {testCategory?.data?.map((category) => (
                <Select.Option key={category.id} value={category.id}>
                  {category.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Also Known As"
            name="also_known_as"
            rules={[{ required: true, message: "Please enter a also known as!" }]}
          >
            <Input.TextArea rows={4} placeholder="Enter also known as" />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: "Please enter a Description!" }]}
          >
            <Input.TextArea rows={4} placeholder="Enter Description" />
          </Form.Item>

          <Form.Item>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Button type="primary" htmlType="submit">
                Submit
              </Button>
              <Button onClick={handleCancel}>Cancel</Button>
            </div>
          </Form.Item>
        </Form>
      )}

      {/* Create Category Modal */}
      <Modal
        title="Create New Category"
        visible={isCategoryModalOpen}
        onCancel={handleCategoryCancel}
        footer={null}
        width="min(520px, 95vw)"
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: "80vh", overflowY: "auto" }}
      >
        <Form
          form={categoryForm}
          layout="vertical"
          onFinish={handleCategorySubmit}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "Please enter category name!" }]}
          >
            <Input placeholder="Enter category name" />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: "Please enter a description!" }]}
          >
            <Input.TextArea rows={3} placeholder="Enter description" />
          </Form.Item>

          <Form.Item
            label="Image"
            name="image"
            rules={[{ required: true, message: "Please upload an image!" }]}
          >
            <Upload beforeUpload={() => false} listType="picture">
              <Button icon={<UploadOutlined />}>Upload Image</Button>
            </Upload>
          </Form.Item>

          <Form.Item>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Button type="primary" htmlType="submit">
                Submit
              </Button>
              <Button onClick={handleCategoryCancel}>Cancel</Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Bulk Upload Modal */}
      <Modal
        title="Bulk Upload Tests"
        visible={isBulkUploadModalOpen}
        onCancel={handleBulkUploadCancel}
        footer={null}
        width="min(800px, 95vw)"
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: "80vh", overflowY: "auto" }}
      >
        <BulkUploadTest
          setTest={setTest}
          Lab={Lab}
          setIsBulkUploadModalOpen={setIsBulkUploadModalOpen}
          baseUrl={baseUrl}
          prodTestUrl={prodTestUrl}
          onSuccess={handleBulkUploadSuccess}
        />
      </Modal>
    </div>
  );
};

export default Test;